import NextButton from "@/components/NextButton";
import PackedIcon from "@/components/icons/PackedIcon";
import Panel from "@/components/Panel";
import CartIcon from "@/components/icons/CartIcon";
import EstimateLineItem from "@/components/EstimateLineItem";
import ArtIcon from "@/components/icons/ArtIcon";
import StepFooter from "@/components/StepFooter";
import StepTitles from "@/components/StepTitles";
import {useAppState} from "@/store";
import {apiUrl, dataLayerPush, dateParts, formatPrice} from "@/helpers";
import DOMPurify from "dompurify";
import {API_ENDPOINTS} from "@/constants";
import {useApi} from "@/api";
import {toast} from "react-toastify";
import {useEffect} from "react";
import Toggle from "@/components/fields/Toggle";
import {useValidation} from "@/validation";

export default function Book({goToStep}) {

    const {state, setState} = useAppState()
    const {api, setLoading} = useApi()
    const {preSubmitValidate} = useValidation()
    const handleSubmit = async () => {
        setLoading(true)
        try {
            preSubmitValidate()

            const response = await api.post(apiUrl(API_ENDPOINTS.book), {
                "requestId": state.requestId,
                "notes": {
                    "from": state.special_origin,
                    "to": state.special_destination
                }
            })
            setState((s) => {
                s.complete = true
            })

            await dataLayerPush('book-confirm-submit', {
                ['book-confirm-selected-amount']: Math.min(state.availability?.price, state.availability?.salePrice),
                ['book-confirm-selected-date']: state.chosen_date?.raw || null,
                ['book-confirm-selected-time']: state.chosen_time?.time || null,
                ['book-confirm-selected-movers']: state.estimate?.movers || 0,
                ['book-confirm-selected-hours']: state.estimate?.totalTime || 0,
                ['book-confirm-selected-trucks']: state.estimate?.trucks || 0,
                ['book-confirm-selected-trip-fees']: state.estimate?.tripFees || 0,
                ['book-assigned-branch']: state.branch?.name || null,
                ['book-estimate-id']: state.customerId || null,
            })

            goToStep(7)
        } catch (e) {
            toast.error(e.message);
        }
        setLoading(false)
    }

    const StepNumber = ({step, label}) => {
        return (
            <div className={"flex items-center font-bold"}>
                <div className={"rounded-full size-[32px] bg-brand-orange heading-h5 text-white flex items-center justify-center mr-[4px]"}>
                    {step}
                </div>
                {label}
            </div>
        )
    }

    useEffect(() => {
        dataLayerPush('book-confirm-view', {
            ['book-assigned-branch']: state.branch?.name || null,
            ['book-estimate-id']: state.customerId || null,
        }).then()
    }, [])

    const date = state.chosen_date && dateParts(state.chosen_date.raw, "long")

    const content = window.ein?.content?.step_6 || {}

    const {title, secondary_title, tertiary_title, special_considerations} = content

    return (
        <div className={"grid grid-cols-4 lg:grid-cols-12 gap-x-[20px] lg:gap-x-[32px] gap-y-[40px]"}>
            <div className="col-span-4 lg:col-span-12 flex flex-col items-center text-center mb-[16px]">
                <PackedIcon className={'size-[80px] mb-[16px]'}/>
                <StepTitles title={title} secondaryTitle={secondary_title} tertiaryTitle={tertiary_title}/>
            </div>
            <div className="col-span-4 lg:col-span-8 lg:col-start-3">
                {state.estimate && (
                    <Panel>
                        <div className={"flex items-center mb-[24px]"}>
                            <CartIcon className={"size-[48px]"}/>
                            <div className={"heading-h3 ml-[16px]"}>
                                Estimate details
                            </div>
                        </div>
                        <div>
                            <EstimateLineItem label={"Estimate ID"} content={state.customerId} />
                            <EstimateLineItem label={"Move Day"} content={`${date.weekday}, ${date.month} ${date.day} @ ${state.chosen_time.time}`}/>
                            <EstimateLineItem label={"Origin"} content={state.from.address}/>
                            <EstimateLineItem label={"Destination"} content={state.to.address}/>
                            <EstimateLineItem label={"Number of Movers"} content={`${state.estimate.movers} Pro${state.estimate.movers === 1 ? '' : 's'}`}/>

                            <EstimateLineItem label={"Hourly Rate"} content={`${formatPrice(state.estimate.movers * state.availability?.adjustedRate || state.estimate?.rate)}`}/>
                            <EstimateLineItem label={"Estimated Hours"} content={`${state.estimate.totalTime} hrs`}/>

                            {state.estimate.tripFees > 0 && (
                                <EstimateLineItem label={"Trip Fees"} content={`${formatPrice(state.estimate.tripFees)}`}/>
                            )}

                            <EstimateLineItem label={"Trucks Needed"} content={`${state.estimate.trucks} x 26’ truck${state.estimate.trucks === 1 ? '' : 's'}`}/>
                            <EstimateLineItem label={"Total"} content={formatPrice(state.chosen_rate)} total={true}/>
                        </div>
                    </Panel>
                )}

                <Panel className={"mt-[56px]"}>
                    <div className={"flex items-center mb-[24px]"}>
                        <ArtIcon className={"size-[48px]"}/>
                        <div className={"heading-h3 ml-[16px]"}>
                            Special considerations
                        </div>
                    </div>
                    {special_considerations && (
                        <div
                            className={"mb-[16px] [&>:last-child]:mb-0"}
                            dangerouslySetInnerHTML={{'__html': DOMPurify.sanitize(special_considerations)}}
                        />
                    )}
                    <StepNumber step={1} label={"Origin considerations"} />
                    <textarea
                        className={"daisy-textarea daisy-textarea-neutral w-full h-[112px] mt-[12px] mb-[24px] placeholder:italic text-base focus:placeholder:text-transparent"}
                        placeholder={"We have a large Pokémon card collection that should be handled as though it is as precious as a newborn baby."}
                        value={state.special_origin || ''}
                        onChange={(e) => setState((s) => {s.special_origin = e.target.value})}
                    ></textarea>
                    <StepNumber step={2} label={"Destination considerations"} />
                    <textarea
                        className={"daisy-textarea daisy-textarea-neutral w-full h-[112px] mt-[12px] placeholder:italic text-base focus:placeholder:text-transparent"}
                        placeholder={"The staircase was clearly designed by someone who was inspired by that couch episode of \"Friends\". You might need to get creative with angles and patience. PIVOT! "}
                        value={state.special_destination || ''}
                        onChange={(e) => setState((s) => {s.special_destination = e.target.value})}
                    ></textarea>
                </Panel>
            </div>


            <div className="col-span-4 lg:col-span-12 flex justify-center">
                <Toggle
                    label={(
                        <span className={"font-bold heading-h6"}>I understand that minimum prices are involved and all website quotes are estimates.</span>
                    )}
                    name={`contact.accept_estimate`}
                    checked={state.contact.accept_estimate || ''}
                    onChange={checked => setState((s) => {s.contact.accept_estimate = checked})}
                />
            </div>

            <div className="col-span-4 lg:col-span-12 flex justify-center">
                <NextButton onClick={handleSubmit} text={'Book your move!'} />
            </div>
            <div className="col-span-4 lg:col-span-12">
                <div className={"text-center mb-[48px]"}>
                    Join <strong>100,000+ happy customers</strong> who made the Smart Move™
                </div>
                <StepFooter/>
            </div>
        </div>
    )
}
